// components
import Button from '@sdb/ui/atoms/Button';
import { Stack, Typography } from '@mui/material';
import {
  DateValidationError,
  PickerChangeHandlerContext
} from '@mui/x-date-pickers-pro';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// queries & mutations
import { useStartRecalculationMutation } from './queries';

// utils
import { addYears, subYears } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { StyledBoxFlexSmallGap } from 'components/shared/Box/Box.styles';
import { IconButton } from '@sdb/planning-ui';
import {
  StyledDatePickerYearPopper,
  StyledHourManagementDatePicker
} from './HourManagement.styles';

const HourManagement = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [yearDate, setYearDate] = useState<Date | null | undefined>(new Date());

  const { mutate: recalculateForYearDate } = useStartRecalculationMutation();

  const onChangeDate = (
    value: Date | Dayjs | null,
    _?: PickerChangeHandlerContext<DateValidationError>
  ) => {
    const asDate = value
      ? dayjs.isDayjs(value)
        ? value.toDate()
        : value
      : null;

    setYearDate(asDate);
    setOpen(false);
  };

  const onClickRecalculate = () => {
    if (!yearDate) {
      return;
    }

    recalculateForYearDate(yearDate);
  };

  return (
    <Stack gap={4}>
      <Typography variant="h5" component="h2">
        {t('adminPortal.realization.recalculateHourManagement.name')}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'SourceSansPro-Regular' }}>
        {t('adminPortal.realization.recalculateHourManagement.description')}
      </Typography>
      <StyledBoxFlexSmallGap>
        <StyledHourManagementDatePicker
          views={['year']}
          label={t('year')}
          value={yearDate}
          open={open}
          onChange={onChangeDate}
          minDate={subYears(new Date(), 10)}
          maxDate={addYears(new Date(), 10)}
          enableAccessibleFieldDOMStructure={false}
          slots={{
            popper: StyledDatePickerYearPopper
          }}
          slotProps={{
            textField: () => {
              return {
                variant: 'outlined',
                size: 'small',
                InputProps: {
                  endAdornment: (
                    <IconButton
                      onClick={() => setOpen((prev) => !prev)}
                      size="small"
                      type="calendar"
                      color="inherit"
                    />
                  )
                }
              };
            }
          }}
        />
        <Button
          variant="outlined"
          onClick={onClickRecalculate}
          disabled={!yearDate}
        >
          {t('adminPortal.realization.recalculateHourManagement.recalculate')}
        </Button>
      </StyledBoxFlexSmallGap>
    </Stack>
  );
};

export default HourManagement;
